import styled from "@emotion/styled";
import { convert } from "@stepfinance/common";
import { darkGrey, formatBalance, greenTag, TagInfo, transparentWhite, white } from "@stepfinance/react";
import CollapsePanel from "antd/lib/collapse/CollapsePanel";
import type { Dispatch, FC, ReactNode, SetStateAction } from "react";
import { useState } from "react";

import { ReactComponent as Arrow } from "../../assets/arrow.svg";
import { ReactComponent as Chevron } from "../../assets/chevron.svg";
import { percentageInParentheses } from "../../constants/regex";
import { useLanguageContext } from "../../contexts/language/language";
import { useMarkets } from "../../contexts/market";
import type { CurrencyContextState } from "../../contexts/swap";
import { useCalculatePriceForDisplayCurrency } from "../../hooks";
import type { JupiterRoute } from "../../utils/jupiter/api";
import { getTokenByMint } from "../../utils/tokenInfo";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { WhiteLabel } from "../shared";
import { StyledTradeCollapse } from "./SwapTradeInfo";

type RouteSelectorProps = {
  routes: JupiterRoute[];
  setSelectedRoute: Dispatch<SetStateAction<JupiterRoute | undefined>>;
  A: CurrencyContextState;
  B: CurrencyContextState;
};

type RouteChoiceProps = {
  setSelectedRoute: Dispatch<SetStateAction<JupiterRoute | undefined>>;
  setSelectedKey: Dispatch<SetStateAction<number>>;
  selectedKey: number;
  routeKey: number;
  route: JupiterRoute;
};

export type RouteMarketInfo = JupiterRoute["marketInfos"][number];

const generatePlatformTag = (labels: string[]) => {
  const res: ReactNode[] = [];
  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    const alreadyHasPercentages = label.includes("%");
    if (alreadyHasPercentages) {
      // This label is formatted similarly to "Orca (Whirlpools) (95%) + Raydium (5%)"
      const splitByPlus = label.split(" + "); // ["Orca (Whirlpools) (95%)", "Raydium (5%)"]
      splitByPlus.forEach((split, x, arr) => {
        const [platform, percentage] = split.split(percentageInParentheses); // ["Orca (Whirlpools) ", "(95%)"]
        res.push(<WhiteLabel key={`${i}-${x}-white`}>{platform}</WhiteLabel>);
        res.push(<DarkGreyLabel key={`${i}-${x}-grey`}>{percentage}</DarkGreyLabel>);
        if (x < arr.length - 1) {
          res.push("+");
        }
      });
    } else {
      // Append (100%) to unsplit routes
      res.push(<WhiteLabel key={`${i}-white`}>{label}</WhiteLabel>);
      res.push(<DarkGreyLabel key={`${i}-grey`}>(100%)</DarkGreyLabel>);
    }
    if (i < labels.length - 1) {
      // Add an arrow between each route
      res.push(<Arrow key={`${i}-arrow`} width={10} height={10} color={white} />);
    }
  }
  return res;
};

const generateRoutePathLabel = (marketInfos: RouteMarketInfo[]) => {
  const res: ReactNode[] = [];
  for (let i = 0; i < marketInfos.length; i++) {
    res.push(<span key={i + "-span"}>{getTokenByMint(marketInfos[i].inputMint)?.symbol ?? ""}</span>);
    res.push(<Arrow key={i + "-arrow"} width={10} height={10} color={transparentWhite} />);
  }
  res.push(
    <span key="final-symbol">{getTokenByMint(marketInfos[marketInfos.length - 1].outputMint)?.symbol ?? ""}</span>,
  );
  return res;
};

const generateExtraRoutesRateRange = (
  extraRoutes: JupiterRoute[],
  tokenADecimals?: number,
  tokenBDecimals?: number,
) => {
  if (extraRoutes.length === 0) {
    return null;
  }

  const bestRate = convert(extraRoutes[0].outAmount, tokenBDecimals) / convert(extraRoutes[0].inAmount, tokenADecimals);
  const worstRate =
    convert(extraRoutes[extraRoutes.length - 1].outAmount, tokenBDecimals) /
    convert(extraRoutes[extraRoutes.length - 1].inAmount, tokenADecimals);

  return (
    <RouteRateSpan>
      <strong>From</strong>
      <span className="number">{bestRate.toFixed(9)}</span>
      <strong>To</strong>
      <span className="number">{worstRate.toFixed(9)}</span>
    </RouteRateSpan>
  );
};

export const RouteSelector: FC<RouteSelectorProps> = ({ routes, setSelectedRoute, A, B }) => {
  const [selectedKey, setSelectedKey] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const {
    currentLanguageData: {
      pages: {
        swapLP: { swapRouteSelector, routeSelectionExpansion },
      },
    },
  } = useLanguageContext();

  const extraRoutesSlice = routes.slice(2);

  return routes.length > 0 ? (
    <RouteSelectorContainer>
      <RoutesFoundSpan>{formatStringWithPlaceholders(swapRouteSelector.routesFound, routes.length)}</RoutesFoundSpan>
      <Spacer />
      <RouteChoiceContainer>
        {routes.slice(0, 2).map((route, i) => (
          <RouteChoice
            route={route}
            key={i}
            routeKey={i}
            selectedKey={selectedKey}
            setSelectedKey={setSelectedKey}
            setSelectedRoute={setSelectedRoute}
          />
        ))}
        {expanded &&
          extraRoutesSlice.map((route, i) => (
            <RouteChoice
              route={route}
              key={i + 2}
              routeKey={i + 2}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
              setSelectedRoute={setSelectedRoute}
            />
          ))}
      </RouteChoiceContainer>
      {extraRoutesSlice.length > 0 && (
        <StyledTradeCollapse
          expandIcon={() => <StyledChevron activeRotation={180} expanded={expanded} />}
          onChange={() => setExpanded(!expanded)}
          defaultActiveKey={0}
        >
          <CollapsePanel
            key={1}
            header={
              <RouteCollapseHeaderContainer>
                <span>
                  <strong>{expanded ? routeSelectionExpansion.showLess : routeSelectionExpansion.showMore}</strong>
                </span>
                {generateExtraRoutesRateRange(extraRoutesSlice, A.mint?.decimals, B.mint?.decimals)}
              </RouteCollapseHeaderContainer>
            }
          >
            <></>
          </CollapsePanel>
        </StyledTradeCollapse>
      )}
    </RouteSelectorContainer>
  ) : null;
};

const RouteChoice: FC<RouteChoiceProps> = ({ route, selectedKey, routeKey, setSelectedRoute, setSelectedKey }) => {
  const { outAmount, marketInfos } = route;
  const isSelected = selectedKey === routeKey;
  const {
    currentLanguageData: {
      pages: {
        swapLP: { swapRouteSelector: selectorText },
      },
    },
  } = useLanguageContext();

  return (
    <RouteChoiceBox
      selected={isSelected}
      onClick={() => {
        setSelectedRoute(route);
        setSelectedKey(routeKey);
      }}
    >
      {routeKey === 0 && (
        <BestPriceTagContainer>
          <TagInfo tagType="text" tagColorOverride={greenTag} uppercase={false} useSpaceMono={false}>
            {selectorText.bestPrice}
          </TagInfo>
        </BestPriceTagContainer>
      )}
      <PoolSplitAndRouteContent marketInfos={marketInfos} />
      <OutputAmountsContent outAmount={outAmount} outMint={marketInfos[marketInfos.length - 1].outputMint} />
    </RouteChoiceBox>
  );
};

const PoolSplitAndRouteContent: FC<{ marketInfos: RouteMarketInfo[] }> = ({ marketInfos }) => {
  const platformTag = generatePlatformTag(marketInfos.map(({ label }) => label));

  return (
    <RouteSplitContainer>
      <PlatformTag>{platformTag}</PlatformTag>
      <DarkGreyLabel bold>{generateRoutePathLabel(marketInfos)}</DarkGreyLabel>
    </RouteSplitContainer>
  );
};

const OutputAmountsContent: FC<{ outAmount: number; outMint: string }> = ({ outAmount, outMint }) => {
  const { priceInUSD } = useMarkets();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const token = getTokenByMint(outMint);
  const coinAmount = convert(outAmount, token?.decimals);
  const currencyValue = coinAmount * (priceInUSD(outMint) ?? 0);
  return token ? (
    <RouteSplitContainer>
      <OutAmount>{formatBalance(coinAmount, token.decimals)}</OutAmount>
      <OutAmountInUSDDisplay>{calculatePriceForDisplayCurrency(currencyValue)}</OutAmountInUSDDisplay>
    </RouteSplitContainer>
  ) : null;
};

export const StyledChevron = styled(Chevron, {
  shouldForwardProp: (prop) => prop !== "expanded" && prop !== "activeRotation",
})<{ expanded?: boolean; activeRotation?: number }>`
  width: 20px;
  height: 20px;
  transition: 300ms;
  ${({ expanded, activeRotation }) => (expanded ? `transform: rotate(${activeRotation ?? "-90"}deg);` : "")}
  path {
    fill: ${transparentWhite};
  }
`;

const RouteChoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
  width: 100%;
`;

const RouteRateSpan = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const RouteCollapseHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  span {
    color: ${transparentWhite};
    font-size: 12px;
  }

  .number {
    font-family: Space Mono;
    font-weight: 400 !important;
  }
`;

const BestPriceTagContainer = styled.div`
  position: absolute;
  top: -11px;
  left: 10px;
`;

const PlatformTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25em;
`;

const OutAmountInUSDDisplay = styled.span`
  color: ${transparentWhite};
  font-family: Space Mono;
  font-size: 12px;
  line-height: 22px;
  text-align: right;
`;

const DarkGreyLabel = styled.span<{ bold?: boolean }>`
  display: flex;
  gap: 6px;
  align-items: center;
  color: ${transparentWhite};
  ${({ bold }) => (bold ? "font-weight: 700;" : "")}
`;

const Spacer = styled.div`
  margin-top: 20px;
`;

const RouteSplitContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const OutAmount = styled.span`
  color: white;
  font-family: Space Mono;
  font-size: 12px;
  font-weight: 700;
  text-align: right;
`;

const RouteChoiceBox = styled.div<{ selected: boolean }>`
  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 64px;
  border: 1px solid ${({ selected }) => (selected ? greenTag : "rgba(120, 120, 120, 0.15)")};
  border-radius: 8px;
  background-color: ${darkGrey};
  padding: 12px 10px;
  transition: 200ms;

  &:hover {
    cursor: pointer;
  }
  * {
    font-size: 12px;
  }
  * > {
    width: 50%;
  }
`;

const RouteSelectorContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RoutesFoundSpan = styled.div`
  color: ${transparentWhite};
  font-size: 12px;
  font-weight: 400;
  line-height: 22px;
`;
